import { useEffect, useState } from "react";
import { api, useAuth } from "../../App";
import { mono, toneOf, PageTitle } from "./ptUi";

// Attendance — tap a cell to cycle D -> P -> X -> A -> L -> clear. Writes
// pt_cadence (id date_userid) in DM's Mongo. Members edit only their own row.

const MARKS = ["D", "P", "X", "A", "L"];
const LABEL = { D: "Delivered", P: "Partial", X: "Missed/Delayed", A: "Working", L: "Leave/Off" };
const monthDays = (m) => { const [y, mo] = m.split("-").map(Number); const n = new Date(y, mo, 0).getDate(); return Array.from({ length: n }, (_, i) => `${m}-${String(i + 1).padStart(2, "0")}`); };
const addMonth = (m, d) => { const [y, mo] = m.split("-").map(Number); const x = new Date(y, mo - 1 + d, 1); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}`; };
const isWeekend = (ds) => { const g = new Date(ds).getDay(); return g === 0 || g === 6; };

export default function PtrackerAttendance() {
  const { user } = useAuth();
  const myId = user?.user_id || user?.id;
  const isLead = ["admin", "srmanager", "manager", "creativedirector", "associatecreativedirector"].includes((user?.role || "").toLowerCase());
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [members, setMembers] = useState([]);
  const [byKey, setByKey] = useState({});
  const days = monthDays(month);
  const today = new Date().toISOString().slice(0, 10);

  const load = () => api.get(`/ptracker/cadence?month=${month}`).then((r) => {
    const map = {}; (r.data.cadence || []).forEach((c) => (map[c.id] = c)); setByKey(map);
  }).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);
  useEffect(() => { api.get("/ptracker/refs/members").then((r) => setMembers(r.data.members || [])).catch(() => {}); }, []);

  const canEdit = (m) => isLead || m.id === myId;
  const rollup = (m) => {
    const c = { D: 0, P: 0, X: 0, A: 0, L: 0 };
    for (const day of days) { const mk = byKey[`${day}_${m.id}`]?.mark; if (mk && c[mk] != null) c[mk]++; }
    const out = c.D + c.P + c.X; // days with an output verdict
    return { ...c, pct: out ? Math.round((c.D / out) * 100) : 0 };
  };
  const rh = { padding: "6px 8px", fontFamily: mono, fontSize: 9, color: "var(--t3)", textAlign: "center", borderLeft: "1px solid var(--bdr)" };
  const cycle = (m, day) => {
    if (!canEdit(m)) return;
    const id = `${day}_${m.id}`;
    const cur = byKey[id]?.mark ?? null;
    const order = [...MARKS, null];
    const next = order[(order.indexOf(cur) + 1) % order.length];
    api.put(`/ptracker/cadence/${id}`, { user_id: m.id, date: day, mark: next, note: byKey[id]?.note || "" }).then(load).catch((e) => alert(e.response?.data?.detail || "Failed"));
  };

  return (
    <div style={{ padding: "24px 28px", maxWidth: 1300, margin: "0 auto" }} data-testid="ptracker-attendance">
      <PageTitle title="Attendance" sub="Tap a cell to cycle D · P · X · A · L. People are from Dream Machine."
        right={
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <span onClick={() => setMonth(addMonth(month, -1))} style={{ cursor: "pointer", color: "var(--t2)", padding: "6px 10px", border: "1px solid var(--bdr)", borderRadius: "var(--radius-sm)" }}>‹</span>
            <span style={{ fontFamily: mono, fontSize: 12, color: "var(--t1)", minWidth: 92, textAlign: "center" }}>{month}</span>
            <span onClick={() => setMonth(addMonth(month, 1))} style={{ cursor: "pointer", color: "var(--t2)", padding: "6px 10px", border: "1px solid var(--bdr)", borderRadius: "var(--radius-sm)" }}>›</span>
          </div>
        } />

      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 12, fontFamily: mono, fontSize: 10, color: "var(--t3)" }}>
        {MARKS.map((mk) => <span key={mk}><span style={{ background: toneOf(mk).bg, color: toneOf(mk).fg, padding: "1px 5px", borderRadius: 3, fontWeight: 700 }}>{mk}</span> {LABEL[mk]}</span>)}
      </div>

      <div style={{ overflowX: "auto", border: "1px solid var(--bdr)", borderRadius: "var(--radius-sm)", background: "var(--s1)" }}>
        <table style={{ borderCollapse: "collapse", fontSize: 11 }}>
          <thead>
            <tr>
              <th style={{ position: "sticky", left: 0, background: "var(--s2)", padding: "8px 12px", textAlign: "left", fontFamily: mono, fontSize: 10, color: "var(--t3)", borderRight: "1px solid var(--bdr)" }}>MEMBER</th>
              {days.map((day) => (
                <th key={day} style={{ padding: "6px 0", width: 30, background: day === today ? "var(--rd)" : isWeekend(day) ? "var(--s3)" : "var(--s2)", color: day === today ? "var(--r)" : "var(--t3)", fontFamily: mono, fontSize: 10 }}>{Number(day.slice(8))}</th>
              ))}
              {["Del", "Par", "Mis", "Wrk", "Lve", "Del%"].map((h) => <th key={h} style={{ ...rh, background: "var(--s2)", fontWeight: 700 }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.id}>
                <td style={{ position: "sticky", left: 0, background: "var(--s1)", padding: "6px 12px", fontFamily: "var(--font-sans)", color: "var(--t1)", whiteSpace: "nowrap", borderRight: "1px solid var(--bdr)", fontWeight: 500 }}>{m.name}</td>
                {days.map((day) => {
                  const c = byKey[`${day}_${m.id}`];
                  const t = c ? toneOf(c.mark) : null;
                  return (
                    <td key={day} onClick={() => cycle(m, day)} title={c?.note || undefined}
                      style={{ textAlign: "center", cursor: canEdit(m) ? "pointer" : "default", background: isWeekend(day) ? "var(--s2)" : "transparent", borderTop: "1px solid var(--bdr)" }}>
                      <span style={{ display: "inline-flex", width: 26, height: 26, alignItems: "center", justifyContent: "center", fontFamily: mono, fontSize: 11, fontWeight: 700,
                        color: t ? t.fg : "var(--t4)", background: t ? t.bg : "transparent", borderRadius: 4 }}>{c?.mark || "·"}</span>
                    </td>
                  );
                })}
                {(() => { const r = rollup(m); return [
                  <td key="d" style={{ ...rh, color: "#0ca30c", fontWeight: 700 }}>{r.D}</td>,
                  <td key="p" style={{ ...rh, color: "#d97706" }}>{r.P}</td>,
                  <td key="x" style={{ ...rh, color: "var(--r)" }}>{r.X}</td>,
                  <td key="a" style={{ ...rh, color: "var(--t2)" }}>{r.A}</td>,
                  <td key="l" style={{ ...rh, color: "var(--t3)" }}>{r.L}</td>,
                  <td key="pct" style={{ ...rh, fontWeight: 700, color: "var(--t1)" }}>{r.pct}%</td>,
                ]; })()}
              </tr>
            ))}
            {members.length === 0 && <tr><td style={{ padding: 30, fontFamily: mono, fontSize: 12, color: "var(--t3)" }}>Loading team…</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
